import React, { useState, useEffect, useCallback } from 'react';
import { RunState, Privilege, VideoTranscodeState } from '../../api';
import { Session, toMillis } from '../../services/sessionModels';
import { transcodeState } from '../../services/sessionsUpload';
import {
  PagingState,
  CustomPaging,
  SortingState,
  Sorting,
  GridColumnExtension,
  SelectionState,
  IntegratedSelection,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableColumnResizing,
  PagingPanel,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';
import CardHeader from '../Util/CardHeader';
import { useSnackbar } from 'notistack';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import SearchOutlined from '@material-ui/icons/SearchOutlined';
import ImportIcon from '@material-ui/icons/SystemUpdateAltOutlined';
import styled from 'styled-components';
import SessionsFilter from './SessionsFilter';
import { Moment } from 'moment';
import { Link } from 'react-router-dom';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import useLocalStorage from '../Util/useLocalStorage';
import SessionDownload from './SessionDownload';
import Date from '../Util/Date';
import SessionUploadButton from './SessionUploadButton';
import SessionPrepareButton from './SessionPrepareButton';
import SessionRunButton from './SessionRunButton';
import SessionLabelButton from './SessionLabelButton';
import useHasPrivilege from '../Util/useHasPrivilege';
import { useFilePicker } from 'use-file-picker';
import axios from 'axios';
import { Button, Tooltip } from '@material-ui/core';
import SessionVisualizeButton from './SessionVisualizeButton';
import { Visibility, CheckCircle, FileCopyOutlined } from '@material-ui/icons';
import SessionDuplicateDialog from './SessionDuplicateDialog';
import jobsService from '../../services/jobs';
import useSessions from './useSessions';

interface Props extends RouteComponentProps {}

export type Diff<T, U> = T extends U ? never : T;

export enum State {
  Upload,
  Prepare,
  Run,
  Label,
}
export type TSession = Session;

const pitch = (row: TSession) => {
  return (row.pitch && row.pitch.name) || '-';
};

export const downloadButton = (row: TSession) => {
  return <SessionDownload session={row} />;
};

const isFullyTranscoded = (session: TSession) => {
  if (!session.videos || session.videos.length === 0) return false;
  const noFinished = session.videos.filter(
    (video) => transcodeState(video) === VideoTranscodeState.Succeeded
  ).length;
  return noFinished === session.videos.length;
};

const isSessionStarted = (session: TSession) => {
  return session.lastRun && session.lastRun.state !== RunState.Created;
};

const isSessionFinishedSuccessfully = (session: TSession) => {
  return session.lastRun?.state === RunState.Stopped && !session.lastRun?.errorMessage;
};

export const determineState = (session: TSession) => {
  if (isFullyTranscoded(session)) {
    if (isSessionStarted(session)) {
      if (isSessionFinishedSuccessfully(session)) {
        return State.Label;
      } else {
        return State.Run;
      }
    } else {
      return State.Prepare;
    }
  }
  return State.Upload;
};

export const created = (row: TSession) => {
  return <Date value={toMillis(row.creationTime)} format="DD-MM-YYYY HH:mm" />;
};

export const played = (row: TSession) => {
  return <Date value={toMillis(row.matchStartTime)} format="DD-MM-YYYY" />;
};

export const homeTeam = (row: TSession) => {
  return row.homeTeam?.name;
};

export const awayTeam = (row: TSession) => {
  return row.awayTeam?.name;
};

const sortingStateColumnExtensions = [
  { columnName: 'components', sortingEnabled: false },
  { columnName: 'runs.bootTime', sortingEnabled: false },
  { columnName: 'dataAvailable', sortingEnabled: false },
  { columnName: 'upload', sortingEnabled: false },
  { columnName: 'prepare', sortingEnabled: false },
  { columnName: 'run', sortingEnabled: false },
  { columnName: 'labeldata', sortingEnabled: false },
  { columnName: 'download', sortingEnabled: false },
];

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Body = styled.div`
  display: flex;
  flex-direction: row;
`;

const FilterContainer = styled.div`
  min-width: 300px;
  flex-grow: 1;
`;

const TableContainer = styled.div`
  flex-grow: 10;
  margin-left: 32px;
  width: calc(100vw - 550px);
`;

export const sortval = (
  columnname: string,
  columns: Array<{ name: string; title: string; getCellValue?: any; sort?: any }>
) => {
  const c = columns.find((column) => column.name === columnname);
  if (c && c.sort) {
    return c.sort;
  }
  return columnname;
};

const Sessions: React.FunctionComponent<Props> = (props) => {
  const [sorting, setSorting] = useLocalStorage<Sorting[]>('availableSessions-sort', [
    { columnName: 'playedAt', direction: 'desc' as 'desc' },
  ]);
  const [page, setPage] = useLocalStorage<number>('availableSessions-page', 0);
  const [pageSize, setPageSize] = useLocalStorage<number>('availableSessions-pagesize', 10);
  const hasWritePermission = useHasPrivilege(Privilege.WriteSession);
  const hasLabelPermission = useHasPrivilege(Privilege.WriteLabelTasks);
  const hasDownloadPermission = useHasPrivilege(Privilege.DownloadData);
  const isAdmin = useHasPrivilege(Privilege.Admin);
  const [columnWidths, setColumnWidths] = useState<Array<{ columnName: string; width: number | string }>>([
    { columnName: 'id', width: 100 },
    { columnName: 'playedAt', width: 100 },
    { columnName: 'teamHome.name', width: 150 },
    { columnName: 'teamAway.name', width: 150 },
    { columnName: 'label', width: 150 },
    { columnName: 'createdAt', width: 150 },
    { columnName: 'upload', width: 100 },
    { columnName: 'prepare', width: 100 },
    { columnName: 'run', width: 100 },
    { columnName: 'labeldata', width: 100 },
    { columnName: 'visualize', width: 100 },
    { columnName: 'pitch', width: 150 },
    { columnName: 'download', width: 64 },
    { columnName: 'approved', width: 120 },
  ]);
  const [completedOnly, setCompletedOnly] = useState(false);
  const [search, setSearch] = useState<string | undefined>();
  const [creationDateRange, setCreationDateRange] = useState<{ startDate: Moment | null; endDate: Moment | null }>({
    startDate: null,
    endDate: null,
  });
  const [pitches, setPitches] = useState<{ [key: string]: boolean }>({});
  const [toBeApprovedOnly, setToBeApprovedOnly] = useState<boolean | null>();
  const [showSearch, setShowSearch] = useState<Boolean>(false);
  const [selection, setSelection] = useState<Array<string | number>>([]);
  const [isDuplicatingSelection, setIsDuplicatingSelection] = useState(false);
  const [tableColumnExtensions] = useState<GridColumnExtension[]>([
    { columnName: 'upload', align: 'center' },
    { columnName: 'prepare', align: 'center' },
    { columnName: 'run', align: 'center' },
    { columnName: 'labeldata', align: 'center' },
  ]);
  let columns: any = [
    {
      name: 'visualize',
      title: <Visibility />,
      getCellValue: (row: TSession) => <SessionVisualizeButton session={row} />,
      prio: 1,
    },
    { name: 'id', title: 'ID', prio: 1 },
    { name: 'createdAt', title: 'Created', getCellValue: created, prio: 3 },
    { name: 'pitch', title: 'Pitch', getCellValue: pitch, sort: 'pitch.name', prio: 5 },
    { name: 'playedAt', title: 'Date', getCellValue: played, prio: 1 },
    { name: 'teamHome.name', title: 'Home', getCellValue: homeTeam, sort: 'teamHome.name', prio: 1 },
    { name: 'teamAway.name', title: 'Away', getCellValue: awayTeam, sort: 'teamAway.name', prio: 1 },
    { name: 'label', title: 'Session label', prio: 1 },
    ...(hasWritePermission
      ? [
          {
            name: 'upload',
            title: 'Upload',
            getCellValue: (row: TSession) => <SessionUploadButton session={row} />,
            prio: 1,
          },
        ]
      : []),
    ...(hasWritePermission
      ? [
          {
            name: 'prepare',
            title: 'Prepare',
            getCellValue: (row: TSession) => <SessionPrepareButton session={row} />,
            prio: 1,
          },
        ]
      : []),
    ...(hasWritePermission
      ? [{ name: 'run', title: 'Run', getCellValue: (row: TSession) => <SessionRunButton session={row} />, prio: 1 }]
      : []),

    ...(hasLabelPermission
      ? [
          {
            name: 'labeldata',
            title: 'Label',
            getCellValue: (row: TSession) => <SessionLabelButton session={row} />,
            prio: 1,
          },
        ]
      : []),
      ...(hasDownloadPermission ? [{ name: 'approved', title: 'Approved', getCellValue: (row: TSession) => {

        if (row.approved) {
          return (
            <Tooltip title="Session is approved">
            <CheckCircle color="secondary" />
          </Tooltip>
          )
        }
        
        return ''
      }, prio: 1 }] : []),
      ...(hasDownloadPermission ? [{ name: 'download', title: ' ', getCellValue: downloadButton, prio: 1 }] : []),
  ];

  const { data, error, refetch } = useSessions({
    page,
    pageSize,
    sortBy: sorting[0] && sortval(sorting[0].columnName, columns),
    sortDescending: sorting[0]?.direction !== 'asc',
    search,
    pitchIds: Object.keys(pitches),
    creationStart: creationDateRange.startDate,
    creationEnd: creationDateRange.endDate,
    approved: toBeApprovedOnly === true ? false : null,
  });

  const { enqueueSnackbar } = useSnackbar();
  const [openFileSelector, { filesContent }] = useFilePicker({
    accept: '.json',
    multiple: false,
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const runIds = data.sessions
        ?.map(x => x.lastRun?.id)
        ?.filter(x => x) as string[] || [] as string[]

    jobsService.subscribeToJob(runIds)
    jobsService.subscribeToSessions(data.sessions.map((x) => String(x.id)))
  }, [data])

  useEffect(() => {
    if (data) {
      if (Math.floor(data.count / pageSize) < page) {
        setPage(0);
      }
    }
  }, [data, page, pageSize, setPage]);

  useEffect(() => {
    filesContent.forEach((file) => {
      enqueueSnackbar('Starting import...', { variant: 'info', autoHideDuration: 3000 });
      axios
        .post(process.env.REACT_APP_PORTAL_URL + '/api/v1/sessions/import', file.content, {
          headers: {
            'content-type': 'application/json'
          },
        })
        .then(function (response) {
          enqueueSnackbar('Successfully imported session', { variant: 'info', autoHideDuration: 3000 });
          console.log(response);
        })
        .catch(function (error) {
          enqueueSnackbar('Failed to import session: ' + (error?.response?.data || error.response.status), {
            variant: 'error',
            autoHideDuration: 3000,
          });
          console.log(error);
        });
    });
  }, [filesContent, enqueueSnackbar]);

  if (error) {
    enqueueSnackbar('Failed to load sessions', { variant: 'error', autoHideDuration: 3000 });
  }

  const toggleShowSearch = useCallback(() => {
    setShowSearch(!showSearch)
  }, [setShowSearch, showSearch])

  const griddata = data?.sessions || [];
  const count = data?.count || 0;
  return (
    <div className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5" style={{ overflow: 'visible' }}>
      <TitleWrapper>
        <div style={{display: 'flex' }}>
          <CardHeader>Available sessions</CardHeader>
          <div style={{ marginTop: '30px'}}>
            <Tooltip title="Show filter">
                <SearchOutlined  style={{ fontSize: 30, marginLeft: '16px', marginTop: '10px', cursor: 'pointer' }}
                onClick={() => {
                  toggleShowSearch();
                }} />
            </Tooltip>
          </div>
        </div>
        
        {hasWritePermission && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isAdmin && selection.length > 0 && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileCopyOutlined />}
                style={{ marginRight: '16px' }}
                onClick={() => setIsDuplicatingSelection(true)}
              >
                Duplicate selected ({selection.length})
              </Button>
            )}
            <Tooltip title="Import session">
              <ImportIcon
                color="primary"
                style={{ fontSize: 40, marginRight: '16px', cursor: 'pointer' }}
                onClick={() => {
                  openFileSelector();
                }}
              />
            </Tooltip>
            <Tooltip title="Create new session">
              <Link to="/session/upload/new">
                <AddCircleIcon color="primary" style={{ fontSize: 40 }} />
              </Link>
            </Tooltip>
           
          </div>
        )}
      </TitleWrapper>
      <SessionDuplicateDialog
        open={isDuplicatingSelection}
        sessionIds={selection.map(String)}
        onClose={() => setIsDuplicatingSelection(false)}
        onDuplicated={() => {
          setSelection([]);
          refetch();
        }}
      />
      <Body>
       {showSearch && <FilterContainer>
          <SessionsFilter
            key="filter"
            creationDateRange={creationDateRange}
            onCreationDateRangeChange={setCreationDateRange}
            search={search}
            onSearchChange={setSearch}
            completedOnly={completedOnly}
            onCompletedOnlyChange={setCompletedOnly}
            pitches={pitches}
            onPitchesChange={setPitches}
            onToBeApprovedOnlyChange={setToBeApprovedOnly}
          />
        </FilterContainer>}
        <TableContainer style={{ marginLeft: showSearch ? '' : '0px'}}>
          <Grid rows={griddata} columns={columns} getRowId={(row: TSession) => row.id}>
            <PagingState
              currentPage={page}
              onCurrentPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
            <SortingState
              sorting={sorting}
              onSortingChange={setSorting}
              columnExtensions={sortingStateColumnExtensions}
            />
            <CustomPaging totalCount={count} />
            {isAdmin && <SelectionState selection={selection} onSelectionChange={setSelection} />}
            {isAdmin && <IntegratedSelection />}
            <Table
              columnExtensions={tableColumnExtensions}
              rowComponent={(row) => (
                <Table.Row
                  {...row}
                  hover
                  // eslint-disable-next-line no-alert
                />
              )}
            />
            <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={setColumnWidths} />
            <TableHeaderRow showSortingControls />
            {isAdmin && <TableSelection showSelectAll />}
            <PagingPanel pageSizes={[10, 25, 50]} />
          </Grid>
        </TableContainer>
      </Body>
    </div>
  );
};

export default withRouter(Sessions);
